/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import {
    ProgressIndicator,
    SecurityHelper,
    FormValidationAlert,
    NeedToKnow,
    Box,
    Spacer,
    ContentBlock,
    Heading,
    useAssetImages,
    Stack,
} from 'sarsaparilla';
import {
    OrderDetailsLayout,
    OrderDetailsTimerNotice,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';
import SharedCaptchaModal from 'shared-ui/components/RecaptchaModal/SharedCaptchaModal';
import { authModalActions } from 'shared-ui/auth/redux';
import {
    useRecaptchaWithFallback,
    RecaptchaCheckbox,
    isRecaptchaChallengeMessage,
} from 'shared-ui/recaptcha';
import { INVENTORY_VEHICLE_PERMIT } from 'ui-field-sales/src/constants/types';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';

import { useParams } from 'react-router';
import * as permitActions from '../actions/permit';
import ReservationDetail from '../components/registration/ReservationDetail';
import { readRuleAsBoolean, renderContent } from '../utils/permitUtils';
import {
    SALES_CURRENTLY_BLOCKED,
    RECAPTCHA_FAILED_MESSAGE,
    RECAPTCHA_BATCH_ACTION,
    BATCH_INCOMPLETE_MESSAGE,
    getBatchPartialMessage,
} from '../utils/utils';
import { progressSteps } from '../constants';

const propTypes = {
    pendingReservations: PropTypes.array,
    postError: PropTypes.object,
    fetchPermitError: PropTypes.object,
    isSavingInProgress: PropTypes.bool,
    cart: PropTypes.object,
    cartError: PropTypes.object,
    permit: PropTypes.object,
    validation: PropTypes.any,
    displayValidationError: PropTypes.bool,
    reservationId: PropTypes.string,
    openLoginModal: PropTypes.func.isRequired,
    pActions: PropTypes.shape({
        clearIssuanceFromStore: PropTypes.func,
        fetchPermit: PropTypes.func,
        clearValidationErrors: PropTypes.func,
        fetchIssuance: PropTypes.func,
        createPendingReservationsFromExisting: PropTypes.func,
        fetchCart: PropTypes.func,
        updatePendingReservation: PropTypes.func,
        addItems: PropTypes.func,
        displayValidationErrors: PropTypes.func,
        editItems: PropTypes.func,
    }).isRequired,
};

const buttons = {
    proceed: { label: 'Proceed to Cart', link: '/cart' },
    inProgress: { label: 'Loading...', link: '/' },
    continue: { label: 'Continue Shopping', link: '/' },
    cancel: { label: 'Remove from Cart', link: '/cart' },
    save: { label: 'Save Changes', link: '/account/reservations' },
};

const continueShopping = () => {
    window.location.assign(buttons.continue.link);
};

export default function PermitRegistrationPageHOC(formComponents) {
    function PermitRegistrationPage(props) {
        const params = useParams();
        const [errors, setErrors] = useState([]);
        const [needToKnow, setNeedToKnow] = useState(false);
        const [isLoadingReservation, setIsLoadingReservation] = useState(true);
        const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
        const [recaptchaFailed, setRecaptchaFailed] = useState(false);
        // Set when a batch failed without telling us what it created. Nothing on
        // this page can be safely resubmitted in that case.
        const [isBatchIncomplete, setIsBatchIncomplete] = useState(false);
        // How many issuances the last failed batch did create. Those have been
        // removed from the page, so what remains is safe to retry.
        const [partialCreatedCount, setPartialCreatedCount] = useState(0);
        const {
            getToken,
            handleRecaptchaError,
            isChallengeRequired,
            isV2Invalid,
            recaptchaCheckboxProps,
        } = useRecaptchaWithFallback();
        const imageObject = useAssetImages({ assetId: params.permitId });
        const inputRefs = [];
        const needToKnowRef = React.createRef();
        const wasGaEventFired = useRef(false);
        const errorAlertRef = useRef(null);
        const wasChallengeModalOpen = useRef(false);

        useEffect(() => {
            const loggedin = SecurityHelper.isLoggedIn();
            if (!loggedin) {
                props.openLoginModal();
                return;
            }
            const { permitId, reservationId } = params;

            const promises = [
                props.pActions.clearIssuanceFromStore(),
                props.pActions.fetchPermit(permitId),
                props.pActions.clearValidationErrors(),
            ];

            if (reservationId) {
                promises.push(
                    props.pActions
                        .fetchIssuance(reservationId, permitId, true)
                        .then(() => {
                            props.pActions.createPendingReservationsFromExisting();
                        })
                );
                promises.push(props.pActions.fetchCart());
            }

            Promise.all(promises).then(() => setIsLoadingReservation(false));
        }, []);

        const getValidationsFromState = () => {
            return props.validation.summary.map((valid) => {
                const path = valid.path.split('.');
                const { controlName, message } = valid.error.props;
                return {
                    inputId: path[path.length - 1],
                    errorText: `${controlName} ${message}`,
                };
            });
        };

        const addToInputRefs = (position) => (ref) => {
            if (ref) {
                inputRefs[position] = ref;
            }
        };

        const removeInputRefs = (position) => {
            inputRefs.splice(position, 1);
        };

        const validate = () => {
            const errorsList = [];
            inputRefs.forEach((ref) => {
                if (ref && ref.validate) {
                    const validation = ref.validate();

                    if (validation?.isInvalid) {
                        errorsList.push({
                            inputId: validation.id,
                            errorText: validation.errorText,
                        });
                    }
                }
            });

            if (needToKnowRef && needToKnowRef.current) {
                const needToKnowValidation = needToKnowRef.current.validate();

                if (needToKnowValidation.isInvalid) {
                    errorsList.push({
                        inputId: needToKnowValidation.id,
                        errorText: needToKnowValidation.errorText,
                    });
                }
            }

            const nonRefErrors = getValidationsFromState();
            if (nonRefErrors.length !== 0) props.pActions.displayValidationErrors();

            errorsList.push(...nonRefErrors);
            setErrors(errorsList);
            return errorsList;
        };

        const handleCheckboxChange = () => {
            const toggledNeedToKnow = !needToKnow;
            setNeedToKnow(toggledNeedToKnow);
            props.pendingReservations.forEach((reservation) => {
                props.pActions.updatePendingReservation(
                    reservation.id,
                    'need_to_know_checked',
                    toggledNeedToKnow
                );
            });
        };

        // Fire a GA Event to pass the additional info about this page to the GA dataLayer
        const facilityId = props.permit.id;
        const agencyName = props.permit.org_code;
        const facilityName = props.permit.name;
        const item_category = INVENTORY_VEHICLE_PERMIT;

        useEffect(() => {
            if (
                window.dataLayer &&
                !wasGaEventFired.current &&
                !isLoadingReservation &&
                agencyName &&
                facilityId &&
                facilityName &&
                item_category
            ) {
                window.dataLayer.push({
                    agencyName,
                    facilityId,
                    facilityName,
                    item_category,
                });

                wasGaEventFired.current = true;
            }
        }, [agencyName, facilityId, facilityName, item_category, isLoadingReservation]);

        // The modal's focus trap defeats the alert's own focus, so scroll the
        // error into view once the modal closes on a displayable error.
        useEffect(() => {
            const justClosed = wasChallengeModalOpen.current && !isChallengeModalOpen;
            wasChallengeModalOpen.current = isChallengeModalOpen;

            const hasDisplayableError =
                recaptchaFailed ||
                isBatchIncomplete ||
                partialCreatedCount > 0 ||
                (props.postError && !isRecaptchaChallengeMessage(props.postError.error));

            if (justClosed && hasDisplayableError && errorAlertRef.current) {
                errorAlertRef.current.scrollIntoView({ block: 'center' });
                errorAlertRef.current.focus();
            }
        }, [
            isChallengeModalOpen,
            recaptchaFailed,
            isBatchIncomplete,
            partialCreatedCount,
            props.postError,
        ]);

        const renderForms = () => {
            return props.pendingReservations.map((res, index) => {
                let rules = get(props.permit, 'rules', []);
                rules = filter(rules, ['item_id', res.division_id]);
                rules = keyBy(rules, 'name');
                const maxGroupSize = get(rules, 'MaxGroupSize.value', 1);

                const allComponents = formComponents.map((Component, sindx) => {
                    const key = `order_detail_component_${sindx}`;
                    const validationIndex = index * 35 + maxGroupSize * 5;
                    return (
                        <Component
                            key={key}
                            permit={props.permit}
                            validation={props.validation}
                            addToInputRefs={addToInputRefs}
                            removeInputRefs={removeInputRefs}
                            vIndex={validationIndex}
                            reservation={res}
                            displayValidationError={props.displayValidationError}
                            pActions={props.pActions}
                            showCommentBox={get(
                                props.permit,
                                'components.comment_box',
                                false
                            )}
                        />
                    );
                });
                return (
                    <fieldset key={res.id}>
                        <div className="rec-nested-wrap">
                            <Stack className="vehicle-permit-detail-form">
                                {allComponents}
                            </Stack>
                        </div>
                    </fieldset>
                );
            });
        };

        const addToCart = async ({ navigate = true } = {}) => {
            const { pendingReservations, pActions, reservationId } = props;
            // A prior batch may have partially succeeded. Resubmitting would
            // duplicate whatever was created, so the cart is the only way forward.
            if (isBatchIncomplete) return undefined;
            const validErrors = validate();
            if (validErrors?.length) return undefined;

            // Clear any stale hard-denial message from a prior attempt.
            setRecaptchaFailed(false);

            // A v2 challenge was previously required - show the checkbox modal
            // before resubmitting. Once it's open, fall through and submit the
            // v2 token the user completed.
            if (isChallengeRequired && !isChallengeModalOpen) {
                setIsChallengeModalOpen(true);
                return undefined;
            }

            // getToken throws if the reCAPTCHA script failed to load. Don't block
            // the purchase on it - submit without a token; the backend decides.
            let recaptchaToken = null;
            try {
                // Must be `create_issuances` - the batch endpoint pins the
                // expected action and verification fails on a mismatch.
                recaptchaToken = await getToken(RECAPTCHA_BATCH_ACTION);
            } catch {
                recaptchaToken = null;
            }
            const recaptchaVersion = isChallengeRequired ? 'V2' : 'V3';

            const reservations = [...pendingReservations];
            if (reservations.length === 1 && reservationId) {
                reservations[0] = { ...reservations[0], id: reservationId };
            }

            const result = await pActions.addItems(reservations, {
                navigate,
                recaptchaToken,
                recaptchaVersion,
            });
            if (result === true) {
                setIsChallengeModalOpen(false);
                return true;
            }

            // addItems resolves the error payload (not `true`) on failure.
            const { shouldRetry, isFailed } = handleRecaptchaError({ data: result });
            if (shouldRetry) {
                // A challenge signal is raised during verification, before any
                // issuance is created, so resubmitting the same batch is safe.
                setIsChallengeModalOpen(true);
            } else {
                // Any other failure: close the modal so it doesn't hide the
                // real order error (e.g. vehicle validation) behind it.
                setIsChallengeModalOpen(false);
                if (isFailed) {
                    setRecaptchaFailed(true);
                } else if (result.createdCount === null) {
                    // Creation is sequential and not transactional, and the
                    // server did not report what it created. Block resubmission
                    // - it could duplicate them - and send the user to the cart.
                    setIsBatchIncomplete(true);
                } else {
                    // addItems dropped the created issuances from the page, so
                    // what's left on screen is exactly what still needs adding.
                    // Accumulate - a retry can partially succeed again.
                    setPartialCreatedCount((prev) => prev + result.createdCount);
                }
            }
            return undefined;
        };

        const closeChallengeModal = () => setIsChallengeModalOpen(false);

        const onClickEdit = async ({ navigate = true } = {}) => {
            const { pendingReservations, pActions, reservationId } = props;
            const validErrors = validate();
            if (validErrors?.length) {
                pActions.displayValidationErrors();
                return;
            }

            if (pendingReservations.length === 1 && reservationId) {
                const reservations = [...pendingReservations];
                reservations[0].id = reservationId;
                const result = await pActions.editItems(reservations, { navigate });
                return result || undefined;
            }
            const result = await pActions.editItems(pendingReservations, { navigate });
            return result || undefined;
        };

        const { reservationId } = params;
        const needToKnowBody = get(props.permit.description, 'need_to_know_order');
        const isSalesEnabled = readRuleAsBoolean('IsEnabled', props.permit);

        const allErrors = [...errors];
        if (props.fetchPermitError) {
            allErrors.push({
                inputId: 'order-details-page',
                errorText: props.fetchPermitError,
            });
        }
        if (props.postError && !isRecaptchaChallengeMessage(props.postError.error)) {
            // reCAPTCHA challenge signals are handled by the fallback modal, not
            // surfaced as a user-facing error.
            allErrors.push({
                inputId: 'order-details-page',
                errorText: props.postError.error,
            });
        }
        if (props.cartError) {
            allErrors.push({ inputId: 'order-details-page', errorText: props.cartError });
        }
        if (props.permit.id && !isSalesEnabled) {
            allErrors.push({
                inputId: 'order-details-page',
                errorText: SALES_CURRENTLY_BLOCKED,
            });
        }
        if (recaptchaFailed) {
            allErrors.push({
                inputId: 'order-details-page',
                errorText: RECAPTCHA_FAILED_MESSAGE,
            });
        }
        if (isBatchIncomplete) {
            allErrors.push({
                inputId: 'order-details-page',
                errorText: BATCH_INCOMPLETE_MESSAGE,
            });
        }
        if (partialCreatedCount > 0) {
            allErrors.push({
                inputId: 'order-details-page',
                errorText: getBatchPartialMessage(partialCreatedCount),
            });
        }

        const summary = (
            <OrderDetailsSummary
                isLoading={isLoadingReservation}
                imageUrl={imageObject.previewImage?.url ?? ''}
                heading={props.permit.name}
                isWaitingForServerResponse={
                    isLoadingReservation || props.isSavingInProgress
                }
                parentName={props.permit.name}
                isProceedToCartDisabled={isBatchIncomplete}
                onProceedToCartClick={reservationId ? onClickEdit : addToCart}
                proceedToCartButtonText={buttons.proceed.label}
                secondaryButtonText={buttons.continue.label}
                onSecondaryButtonClick={continueShopping}
                showBuyNow
                reservationId={reservationId}
            >
                <ReservationDetail
                    permit={props.permit}
                    reservations={props.pendingReservations}
                />
            </OrderDetailsSummary>
        );

        return (
            <div id="order-details-page" className="vehicle-permit order-details-page">
                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator
                        eventToReportToGA={gaReportingCartReservationsForStepper(
                            props?.cart?.cart?.reservations,
                            progressSteps,
                            0
                        )}
                        shouldReportToGA
                        steps={progressSteps}
                        activeStep={1}
                    />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={summary}>
                        <div ref={errorAlertRef} tabIndex={-1}>
                            <FormValidationAlert errors={allErrors} />
                        </div>
                        <Heading headingLevel={1} appearance="h3">
                            Order Details
                        </Heading>
                        <OrderDetailsTimerNotice />
                        <OrderDetailsSection
                            heading={props.permit.name}
                            isLoading={isLoadingReservation}
                        >
                            <form id="form-name">
                                {renderForms()}
                                <fieldset>
                                    <NeedToKnow
                                        ref={needToKnowRef}
                                        siteName={props.permit.name}
                                        isChecked={needToKnow}
                                        onChange={handleCheckboxChange}
                                    >
                                        <div className="rec-information-head">
                                            <h2 className="form-section-header">
                                                Facility Information
                                            </h2>
                                        </div>
                                        {renderContent(needToKnowBody)}
                                    </NeedToKnow>
                                </fieldset>
                            </form>
                        </OrderDetailsSection>
                    </OrderDetailsLayout>
                </ContentBlock>
                {isChallengeModalOpen && (
                    <SharedCaptchaModal
                        isOpen={isChallengeModalOpen}
                        onRequestClose={closeChallengeModal}
                        onContinue={() => addToCart()}
                        showCancelButton
                        isContinueDisabled={isV2Invalid}
                        isContinueLoading={props.isSavingInProgress}
                    >
                        Please complete the challenge below to continue with your permit.
                        <RecaptchaCheckbox
                            {...recaptchaCheckboxProps}
                            isFallbackOnly
                            action={RECAPTCHA_BATCH_ACTION}
                        />
                    </SharedCaptchaModal>
                )}
            </div>
        );
    }

    PermitRegistrationPage.displayName = 'PermitRegistrationPage';
    PermitRegistrationPage.propTypes = propTypes;

    function mapDispatchToProps(dispatch) {
        return {
            pActions: bindActionCreators(permitActions, dispatch),
            openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
        };
    }

    function select(state) {
        return {
            permit: state.vehiclepermit.permit.result,
            fetchPermitError: state.vehiclepermit.permit.fetch_err,
            pendingReservations: state.vehiclepermit.pendingReservations.list,
            fetchIssuanceError: state.vehiclepermit.reservation.fetch_err,
            isSavingInProgress: state.vehiclepermit.reservation.inProgress,
            postError: state.vehiclepermit.reservation.error,
            validation: state.vehiclepermit.validation,
            displayValidationError:
                state.vehiclepermit.pendingReservations.displayValidation,
            cart: state.vehiclepermit.cart,
            cartError: state.vehiclepermit.cart_err,
        };
    }

    return connect(select, mapDispatchToProps)(PermitRegistrationPage);
}
